import { useCallback, useContext, useMemo } from "react";
import {
  HeaderBar,
  HStack,
  ReorderableList,
  ReorderableListItem,
  SearchField,
  Spacer,
  Tag,
  TriggerButton,
  TriggerButtonGroup,
  VStack,
} from "widgets";

import type { TrackConfiguration, TrackGroup } from "../../types.ts";
import { useGenomePanel } from "./hooks/use-genome-panel.ts";
import { TrackConfigurationContext } from "./TrackConfigurationContext.ts";
import style from "./TracksConfiguration.module.css";

export type GroupType = { name: string } & TrackGroupsWithDuplicateMembers;

export const GenomePanel = () => {
  const {
    trackConfiguration,
    setTrackConfiguration,
    setSelectedGroupIds,
    selectedGroupIds,
    groupsToDelete,
    setGroupsToDelete,
  } = useContext(TrackConfigurationContext);

  const { alignedTrackGroupIds, filteredTrackGroups, setFilterTerm } = useGenomePanel();

  const markedGroups = useMemo(
    () => markDuplicates(filteredTrackGroups, groupsToDelete),
    [filteredTrackGroups, groupsToDelete],
  );

  const reorderedGroups = useMemo(() => {
    return [...markedGroups].map((group) => ({
      name: group.contigName,
      ...group,
    }));
  }, [markedGroups]);

  const handleReorder = useCallback(
    (items: GroupType[]) => {
      const newTrackConfiguration: TrackConfiguration = new Map(
        items
          .filter((groupType) => trackConfiguration.has(groupType.id))
          .map((groupType) => {
            const trackGroup = trackConfiguration.get(groupType.id)!;
            return [groupType.id, trackGroup];
          }),
      );
      setTrackConfiguration(newTrackConfiguration);
    },
    [setTrackConfiguration, trackConfiguration],
  );

  return (
    <VStack spacing={0} className={style.genomePanel} style={{ borderRight: "solid 1px var(--ui-border)" }}>
      <HeaderBar
        title="Groups"
        asPanelHeader
        actions={[
          <TriggerButtonGroup key="select-all">
            <TriggerButton
              label="All"
              showLabel
              style={{ height: 26, padding: "0 10px" }}
              icon={<div />}
              key={"select-all-genomes"}
              onPress={() => {
                setSelectedGroupIds([...filteredTrackGroups.keys()]);
              }}
            />
            <TriggerButton
              label="None"
              showLabel
              style={{ height: 26, padding: "0 10px" }}
              icon={<div />}
              key={"clear-selection"}
              onPress={() => {
                setSelectedGroupIds([]);
              }}
            />
            <TriggerButton
              label="Aligned"
              showLabel
              style={{ height: 26, padding: "0 10px" }}
              icon={<div />}
              key={"select-all-aligned-genomes"}
              isDisabled={alignedTrackGroupIds === undefined}
              onPress={() => {
                if (alignedTrackGroupIds !== undefined) {
                  setSelectedGroupIds(alignedTrackGroupIds);
                }
              }}
            />
          </TriggerButtonGroup>,
          <SearchField key="search-field-genome" width={120} onChange={setFilterTerm} />,
        ]}
      />
      {reorderedGroups.length === 0 ? (
        <EmptyGenomeSearch />
      ) : (
        <div className={style.configList}>
          <ReorderableList
            items={reorderedGroups}
            key={"reorderable-track-group-in-dialog"}
            onReorder={handleReorder}
            renderDragPreview={renderDragPreview}
            selectionMode="multiple"
            selectionBehavior="toggle"
            selectedKeys={selectedGroupIds}
            onSelectionChange={(keys) => {
              if (keys !== "all") {
                setSelectedGroupIds([...keys.values()].map(String));
              }
            }}
          >
            {({ id, organismName, contigName, isDuplicate }) => (
              <ReorderableListItem
                id={id}
                key={`dialog-track-group-${id}`}
                className={style.trackGroupItem}
                style={({ isSelected }) => ({
                  backgroundColor: isSelected ? "var(--active-background)" : undefined,
                  padding: "10px 10px 10px 5px",
                  borderRadius: 3,
                })}
              >
                <HStack
                  style={{
                    justifyContent: "space-between",
                    width: "100%",
                    marginLeft: 10,
                  }}
                >
                  <VStack spacing={1}>
                    <div className={style.title}>{organismName}</div>
                    <div className={style.subtitle}>{contigName}</div>
                  </VStack>
                  <Spacer />
                  <HStack>
                    {!!isDuplicate && (
                      <TriggerButton
                        icon="delete"
                        label=""
                        aria-label="Remove"
                        onPress={() => {
                          if (selectedGroupIds.includes(id)) {
                            setSelectedGroupIds(selectedGroupIds.filter((id) => id !== id));
                          }

                          setGroupsToDelete((cur) => [...cur, id]);
                        }}
                      />
                    )}
                    <Tag
                      label={
                        trackConfiguration.get(id)!.defaultTypes.length +
                        trackConfiguration.get(id)!.nuFeatureTypes.length
                      }
                    />
                  </HStack>
                </HStack>
              </ReorderableListItem>
            )}
          </ReorderableList>
        </div>
      )}
    </VStack>
  );
};

const EmptyGenomeSearch = () => {
  return (
    <div className={style.emptyTrackSelection}>
      <div>No genomes or contigs found</div>
    </div>
  );
};

interface TrackGroupsWithDuplicateMembers {
  id: string;
  organismName: string;
  contigName: string;
  isDuplicate: boolean;
}

type TrackGroupsWithDuplicate = Set<TrackGroupsWithDuplicateMembers>;

function markDuplicates(trackGroups: Map<string, TrackGroup>, groupsToDelete: string[]): TrackGroupsWithDuplicate {
  const deleteSet = new Set(groupsToDelete);
  const validGroups = Array.from(trackGroups.entries()).filter(([groupId]) => !deleteSet.has(groupId));
  const organismCounts = new Map<number, number>();

  for (const [, { organismId }] of validGroups) {
    organismCounts.set(organismId, (organismCounts.get(organismId) ?? 0) + 1);
  }

  return new Set(
    validGroups.map(([groupId, { organismId, organismName, contigName }]) => ({
      id: groupId,
      organismName,
      contigName,
      isDuplicate: organismCounts.get(organismId)! > 1,
    })),
  );
}

const renderDragPreview = (groupType: GroupType) => (
  <div style={{ width: "100%", opacity: 0.9 }}>
    <VStack spacing={1}>
      <div className={style.title}>{groupType.organismName}</div>
      <div className={style.subtitle}>{groupType.contigName}</div>
    </VStack>
  </div>
);
